import { useCallback, useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import {
    faAddressBook,
    faEdit,
    faPlus,
    faSearch,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import MiddleEllipsis from 'react-middle-ellipsis';
import { useDebouncedValue } from 'assets/js/hooks/useDebouncedValue';
import type { ContactFieldsFragment } from '@/graphql/types';
import Table from '../../table/Table';
import { useOrderAndPaginationControl } from '../../table/hooks';
import { useUpdateSearchParams } from '../../../hooks/useUpdateSearchParams';
import Pagination from '../../table/Pagination';
import { BasePage } from '../BasePage';
import { IconButton } from '../../form/Button';
import { useContactMutations } from './graphql/useContactMutations';
import { useContactQueryGenerator } from './graphql/useContactQueryGenerator';
import { format } from '../../../utils/formatting';
import { ContactModal } from './components/ContactModal/ContactModal';
import { ContactsToolbar } from './components/ContactsToolbar/ContactsToolbar';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import { EmptyState } from '../../emptyState/EmptyState';
import {
    NotificationsProvider,
    useNotifications,
    NOTIFICATION_VARIANT,
} from '../../notifications/Notifications';
import { useConfirm } from '../../confirm/Confirm';

interface ContactActionsProps {
    row: ContactFieldsFragment;
    isDeleting: boolean;
    onEdit: (row: ContactFieldsFragment) => void;
    onDelete: (row: ContactFieldsFragment) => void;
}

function ContactActions({ row, isDeleting, onEdit, onDelete }: ContactActionsProps) {
    const editLabel = `${gettext('EDIT_CONTACT')} ${row.name}`;
    const deleteLabel = `${gettext('DELETE_CONTACT')} ${row.name}`;

    return (
        <div className="space-x-2">
            <IconButton
                disabled={isDeleting}
                icon={faEdit}
                ariaLabel={editLabel}
                title={editLabel}
                onClick={() => onEdit(row)}
            />
            <IconButton
                disabled={isDeleting}
                icon={faTrash}
                ariaLabel={deleteLabel}
                title={deleteLabel}
                onClick={() => onDelete(row)}
            />
        </div>
    );
}

function EllipsisCell({ value }: { value: string }) {
    return (
        <div className="whitespace-nowrap" title={value}>
            <MiddleEllipsis>
                <span>{value}</span>
            </MiddleEllipsis>
        </div>
    );
}

function renderNoResultsMessage(searchTerm: string) {
    const msg = interpolate(gettext('CONTACTS_SEARCH_NO_RESULTS'), { term: searchTerm }, true);
    const parts = msg.split(`'${searchTerm}'`);

    return (
        <>
            {parts[0]}
            <span className="break-all font-bold text-gray-900">&apos;{searchTerm}&apos;</span>
            {parts[1]}
        </>
    );
}

const EMPTY_STATE_VARIANT = 'brand';
const NO_RESULTS_EMPTY_STATE_ICON = faSearch;
const NO_CONTACTS_EMPTY_STATE_ICON = faAddressBook;
const NO_CONTACTS_EMPTY_STATE_ACTION_ICON = faPlus;
export const CONTACTS_TABLE_SORT = {
    tableId: 'contacts',
    sortableFields: ['name', 'createdAt'],
} as const;

export function ContactsPage(): JSX.Element {
    const { showBoundary } = useErrorBoundary();
    const confirm = useConfirm();
    const { addNotification } = useNotifications();

    const { searchParams, updateSearchParams } = useUpdateSearchParams();
    const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '');
    const debouncedSearch = useDebouncedValue(searchInput);
    const orderAndPaginationControl = useOrderAndPaginationControl(CONTACTS_TABLE_SORT);

    const { contacts, loading, error, updating } = useContactQueryGenerator(
        orderAndPaginationControl,
        debouncedSearch
    );

    const { remove: removeContact, isDeleting } = useContactMutations();

    const [activeContact, setActiveContact] = useState<ContactFieldsFragment | null | undefined>(
        undefined
    );
    const isModalOpen = activeContact !== undefined;

    useEffect(() => {
        updateSearchParams('search', debouncedSearch);
    }, [debouncedSearch, updateSearchParams]);

    useEffect(() => {
        if (error) showBoundary(error);
    }, [error, showBoundary]);

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchInput(value);
            orderAndPaginationControl.setCurrentPage(1);
        },
        [orderAndPaginationControl]
    );

    const performDelete = useCallback(
        async (contact: ContactFieldsFragment) => {
            const result = await removeContact(contact.id);

            if (result.success) {
                addNotification({
                    message: interpolate(
                        gettext('CONTACT_DELETE_SUCCESS'),
                        { name: result.contact.name },
                        true
                    ),
                    variant: NOTIFICATION_VARIANT.SUCCESS,
                });
            } else if (result.mutationError) {
                addNotification({
                    message: result.mutationError,
                    variant: NOTIFICATION_VARIANT.ERROR,
                });
            }
        },
        [removeContact, addNotification]
    );

    const handleDeleteClick = useCallback(
        (contact: ContactFieldsFragment) => {
            confirm({
                message: interpolate(
                    gettext('CONTACT_DELETE_CONFIRMATION'),
                    { name: contact.name },
                    true
                ),
                onConfirm: () => void performDelete(contact),
            });
        },
        [confirm, performDelete]
    );

    const columns = useMemo(
        () => [
            {
                key: 'name',
                title: gettext('NAME'),
                className: 'w-[20%]',
                render: (_column: unknown, row: ContactFieldsFragment) => (
                    <EllipsisCell value={row.name} />
                ),
            },
            {
                key: 'email',
                title: gettext('E-mail address'),
                className: 'w-[20%]',
                render: (_column: unknown, row: ContactFieldsFragment) => (
                    <EllipsisCell value={row.email || '-'} />
                ),
            },
            {
                key: 'phoneNumber',
                title: gettext('Phone number'),
                className: 'w-[15%]',
                render: (_column: unknown, row: ContactFieldsFragment) => (
                    <p>{row.phoneNumber || '-'}</p>
                ),
            },
            {
                key: 'createdAt',
                title: gettext('CREATED_AT'),
                className: 'w-[15%]',
                render: (_column: unknown, row: ContactFieldsFragment) => (
                    <p data-datetime={row.createdAt} data-datetime-target="text">
                        {format(row.createdAt, { useBrowserTimezone: true })}
                    </p>
                ),
            },
            {
                key: 'actions',
                title: (
                    <span className="space-x-2">
                        {(updating || isDeleting) && (
                            <LoadingSpinner className="h-5 align-middle text-gray-400" />
                        )}
                        {orderAndPaginationControl.showPagination && (
                            <Pagination orderAndPaginationControl={orderAndPaginationControl} />
                        )}
                    </span>
                ),
                className: 'w-[30%]',
                render: (_: unknown, row: ContactFieldsFragment) => (
                    <ContactActions
                        row={row}
                        isDeleting={isDeleting}
                        onEdit={setActiveContact}
                        onDelete={handleDeleteClick}
                    />
                ),
            },
        ],
        [handleDeleteClick, isDeleting, orderAndPaginationControl, updating]
    );

    const isLoading = loading || updating;
    const hasContacts = !!contacts.length;
    const showTable = hasContacts || isLoading;

    return (
        <BasePage title={gettext('ADDRESS_BOOK')} className="main-container-new pb-4">
            <ContactsToolbar
                searchValue={searchInput}
                onSearchChange={handleSearchChange}
                onNewContact={() => setActiveContact(null)}
                isActionDisabled={isModalOpen}
            />
            {showTable && (
                <Table
                    data={contacts}
                    columns={columns}
                    orderAndPaginationControl={orderAndPaginationControl}
                    loading={loading || updating}
                    wrapperClasses="[&_table]:table-fixed"
                    getRowKey={(row) => row.id}
                />
            )}
            {!isLoading && debouncedSearch && !hasContacts && (
                <EmptyState
                    variant={EMPTY_STATE_VARIANT}
                    iconFontAwesome={NO_RESULTS_EMPTY_STATE_ICON}
                    header={gettext('NO_RESULTS_FOUND')}
                    description={renderNoResultsMessage(debouncedSearch)}
                    actionButtonText={gettext('CLEAR_SEARCH')}
                    actionButtonMethod={() => handleSearchChange('')}
                />
            )}
            {!isLoading && !debouncedSearch && !hasContacts && (
                <EmptyState
                    variant={EMPTY_STATE_VARIANT}
                    iconFontAwesome={NO_CONTACTS_EMPTY_STATE_ICON}
                    header={gettext('CONTACTS_EMPTY_HEADING')}
                    description={gettext('CONTACTS_EMPTY_BODY')}
                    actionButtonText={gettext('CONTACTS_EMPTY_ACTION')}
                    actionButtonIconFontAwesome={NO_CONTACTS_EMPTY_STATE_ACTION_ICON}
                    actionButtonMethod={() => setActiveContact(null)}
                />
            )}
            {isModalOpen && (
                <NotificationsProvider registerGlobalRef={false}>
                    <ContactModal
                        open={true}
                        existingContact={activeContact ?? undefined}
                        onClose={() => setActiveContact(undefined)}
                    />
                </NotificationsProvider>
            )}
        </BasePage>
    );
}
